import { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { resolveQuality, useScene } from "../store";
import { milkyWayTexture } from "./materials";
import { loadMilkyWay } from "./realTextures";

/** 星野（点）+ 银河带（按真实银道面朝向的等距柱状贴图）。均以相机为中心的大球，不参与拾取。 */
export function Background() {
  const procedural = useMemo(() => milkyWayTexture(), []);
  const q = resolveQuality(useScene((s) => s.quality));
  const [real, setReal] = useState<THREE.Texture | null>(null);
  // 真实银河贴图（银道坐标 → 黄道坐标重投影）；加载或处理失败则保留程序生成的背景
  useEffect(() => {
    let on = true;
    const size = q === "high" ? { w: 4096, h: 2048 } : { w: 2048, h: 1024 };
    loadMilkyWay(size).then((t) => { if (on && t) setReal(t); }).catch((e) => console.warn("[milkyway]", e));
    return () => { on = false; };
  }, [q]);
  const sky = real ?? procedural;
  const tint = useMemo(() => new THREE.Color(real ? 2.6 : 1, real ? 2.6 : 1, real ? 2.6 : 1), [real]); // 真实贴图本身很暗，提亮到可见（>1 的颜色乘数）
  const stars = useMemo(() => {
    const N = 2500, pos = new Float32Array(N * 3), col = new Float32Array(N * 3);
    let s = 7; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
    for (let i = 0; i < N; i++) {
      const u = rnd() * 2 - 1, th = rnd() * Math.PI * 2, r = Math.sqrt(1 - u * u), R = 6000;
      pos[i * 3] = r * Math.cos(th) * R; pos[i * 3 + 1] = u * R; pos[i * 3 + 2] = r * Math.sin(th) * R;
      const h = rnd(), b = 0.35 + rnd() * 0.65, m = rnd() < 0.04 ? 1 : 0.55;
      const c = h < 0.2 ? [1, 0.82, 0.68] : h < 0.4 ? [0.68, 0.8, 1] : [1, 1, 1];
      col[i * 3] = c[0]! * b * m; col[i * 3 + 1] = c[1]! * b * m; col[i * 3 + 2] = c[2]! * b * m;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3)); g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    return g;
  }, []);
  return (
    <group renderOrder={-10}>
      <mesh raycast={() => null} scale={[1, 1, 1]}>
        <sphereGeometry args={[7000, 48, 24]} />
        <meshBasicMaterial map={sky} color={tint} side={THREE.BackSide} depthWrite={false} toneMapped={false} />
      </mesh>
      <points geometry={stars} raycast={() => null}>
        <pointsMaterial size={1.5} sizeAttenuation={false} vertexColors depthWrite={false} toneMapped={false} />
      </points>
    </group>
  );
}
